"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, CircleAlert, Loader } from "lucide-react";
function SkarpeIndlaegConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    isRefetching
  } = usePosts();
  if (loading) {
    return <div className="flex flex-col gap-8 py-12">
        {[...Array(5)].map((_, i) => <div key={i} className="w-full animate-pulse border-b border-border pb-8" data-index={i}>
            <div className="flex flex-col gap-4 md:flex-row md:gap-8">
              <div className="h-24 w-24 shrink-0 bg-muted/50" />
              <div className="flex-1 space-y-4">
                <div className="h-8 w-3/4 bg-muted/50" />
                <div className="h-4 w-full bg-muted/30" />
                <div className="h-4 w-2/3 bg-muted/30" />
              </div>
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-20 text-center">
        <CircleAlert className="mb-4 h-10 w-10 text-destructive" />
        <p className="text-lg font-medium text-destructive">Der opstod en fejl ved indlæsning af indlæg.</p>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="py-20 text-center">
        <p className="text-xl font-serif text-muted-foreground">Ingen indlæg fundet.</p>
      </div>;
  }
  return <div className="flex flex-col">
      {}
      <div className="mb-8 border-b-4 border-border pb-2">
        <div className="flex items-end justify-between">
          <h3 className="font-mono text-sm uppercase tracking-widest text-muted-foreground">Dato</h3>
          <h3 className="font-mono text-sm uppercase tracking-widest text-muted-foreground">Artikel</h3>
        </div>
      </div>

      <div className="flex flex-col">
        {posts.map((post, i) => {
        const dateObj = post.date ? new Date(post.date) : new Date();
        const day = dateObj.getDate().toString().padStart(2, "0");
        const month = dateObj.toLocaleString("da-DK", {
          month: "short"
        }).toUpperCase();
        const year = dateObj.getFullYear();
        return <article key={post.id} data-index={i} className="group relative border-b border-border transition-colors duration-300 hover:bg-muted/20">
              <Link to={post.link || "#"} className="flex flex-col gap-6 py-8 md:flex-row md:gap-12">
                {}
                <div className="flex shrink-0 flex-row items-baseline gap-2 md:w-32 md:flex-col md:items-start md:gap-0">
                  <span className="font-serif text-5xl font-bold leading-none text-foreground/90 group-hover:text-primary">
                    {day}
                  </span>
                  <div className="flex flex-col">
                    <span className="font-mono text-sm font-bold tracking-wider text-muted-foreground">
                      {month}
                    </span>
                    <span className="font-mono text-xs text-muted-foreground/60">
                      {year}
                    </span>
                  </div>
                </div>

                {}
                <div className="flex flex-1 flex-col gap-3">
                  <h2 className="font-serif text-2xl font-bold leading-tight text-foreground group-hover:underline decoration-primary decoration-2 underline-offset-4 md:text-3xl">
                    {post.title}
                  </h2>
                  
                  {post.excerpt && <div className="line-clamp-3 font-sans text-base leading-relaxed text-muted-foreground md:text-lg" dangerouslySetInnerHTML={{
                __html: post.excerpt
              }} />}
                  
                  <div className="mt-2 flex items-center gap-2 text-sm font-medium text-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <span>Læs hele indlægget</span>
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </Link>
            </article>;
      })}
      </div>

      {}
      <div className="mt-12 flex items-center justify-between border-t-4 border-border pt-6">
        <Button variant="outline" onClick={prevPage} disabled={!hasPrev || isRefetching} className="min-w-[120px] border-2 border-border bg-transparent hover:bg-muted/20">
          {isRefetching ? <Loader className="mr-2 h-4 w-4 animate-spin" /> : <ArrowLeft className="mr-2 h-4 w-4" />}
          Forrige
        </Button>
        
        <span className="font-mono text-xs tracking-widest text-muted-foreground uppercase">
          Side {usePosts().currentPage} af {usePosts().totalPages}
        </span>

        <Button variant="outline" onClick={nextPage} disabled={!hasNext || isRefetching} className="min-w-[120px] border-2 border-border bg-transparent hover:bg-muted/20">
          {isRefetching ? <Loader className="mr-2 h-4 w-4 animate-spin" /> : <>
              Næste <ArrowRight className="ml-2 h-4 w-4" />
            </>}
        </Button>
      </div>
    </div>;
}
export default function SkarpeIndlaeg() {
  const wp_query_params = {
    post_type: "post",
    per_page: 10,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="skarpe-indlæg" className="dark bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto max-w-4xl px-4 md:px-6">
        <div className="mb-16 border-b-2 border-foreground pb-6">
          <h2 className="font-serif text-4xl font-black uppercase tracking-tight text-foreground md:text-6xl lg:text-7xl">
            Skarpe Indlæg
          </h2>
          <p className="mt-4 max-w-2xl font-mono text-sm text-muted-foreground md:text-base">
            Arkivet over kompromisløse debatindlæg. Sandheden råt for usødet.
          </p>
        </div>

        <PostsProvider wp_query={wp_query} className="w-full">
          <SkarpeIndlaegConsumer />
        </PostsProvider>
      </div>
    </section>;
}